'use client';

import ControlledButton from '@umodeapporg/ui/components/Form/ControlledButton';
import ControlledTooltip from '@umodeapporg/ui/components/UI/ControlledTooltip';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import { formatNumber } from '@/app/ops/helpers/formatters';
import { formatPercentual } from '@/app/relatorios/helpers/formatRelatorios';
import { Notice } from '@/components/surface/Notice';
import { ErroPromessa } from '@/domain/relatorios/interfaces/relatorios';
import { cn } from '@/lib/utils';

import { ICelulaDaVisao } from '../helpers/visaoExecutiva';
import { CARTEIRA_HREF, carteiraDaCelulaHref } from '../helpers/visaoExecutivaLinks';
import { BarraSegmentada } from './BarraSegmentada';
import { BlocoCard } from './BlocoCard';
import { EstadoDoBloco } from './EstadoDoBloco';

const LINHA = 'flex w-full flex-col gap-1 rounded-md px-2 py-1.5 text-left';

interface PontualidadeBlocoProps {
  celulas: Array<ICelulaDaVisao>;
  /**
   * O prometido contra entregue dos Relatórios, que é a pontualidade REALIZADA. `null`
   * enquanto o relatório não chegou; com zero entregas classificadas, o rodapé volta a
   * dizer o que falta acontecer em vez de mostrar 0%, que leria como fábrica atrasada.
   */
  realizada: ErroPromessa | null;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  className?: string;
}

/**
 * Quanto da carteira de hoje sai no prazo, célula por célula. É pontualidade PROJETADA: a
 * data que o motor dá ao cliente comparada com o prazo do pedido, pela MESMA regra do
 * risco.
 *
 * Clicar numa célula abre a Carteira dela com as OPs que estouram o prazo em cima: o
 * percentual diz o tamanho do problema, e a lista diz quais pedidos são.
 *
 * No rodapé, a REALIZADA: das saídas marcadas como entrega, quantas chegaram dentro da
 * primeira promessa. Ela vem do MESMO cálculo dos Relatórios (`erroPromessa`), porque
 * duas contas para a mesma frase fariam as duas telas discordarem na reunião.
 */
export function PontualidadeBloco({ celulas, realizada, loading, failed, onRetry, className }: PontualidadeBlocoProps) {
  const { t } = useTranslation('common');
  const router = useRouter();
  /** Zero entrega medida não é 0% de pontualidade: é ainda não haver o que medir. */
  const temRealizada = realizada !== null && realizada.ops > 0;

  return (
    <BlocoCard
      pergunta={t('visaoExecutiva.pontualidade.pergunta')}
      fonte={t('visaoExecutiva.pontualidade.projetada')}
      acao={{ label: t('visaoExecutiva.pontualidade.abrir'), href: CARTEIRA_HREF }}
      className={className}
    >
      <EstadoDoBloco
        loading={loading}
        failed={failed}
        vazio={celulas.length === 0}
        vazioTexto={t('visaoExecutiva.pontualidade.vazio')}
        onRetry={onRetry}
      >
        <ul className="flex animate-fade-in flex-col gap-2">
          {celulas.map(({ celula, pontualidade }) => (
            <li key={celula} className="flex min-w-0">
              <ControlledTooltip content={t('visaoExecutiva.pontualidade.irParaCelula', { celula })}>
                <ControlledButton
                  defaultHtmlButton
                  type="button"
                  onClick={() => router.push(carteiraDaCelulaHref(celula))}
                  className={cn(LINHA, 'interactive hover:bg-muted')}
                >
                  <div className="flex w-full items-baseline justify-between gap-2">
                    <span className="truncate text-xs font-semibold text-foreground">{celula}</span>
                    <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
                      {t('visaoExecutiva.pontualidade.ops', {
                        pct: formatPercentual(pontualidade.pctOps),
                        noPrazo: formatNumber(pontualidade.opsNoPrazo),
                        total: formatNumber(pontualidade.ops),
                      })}
                    </span>
                  </div>

                  {/* O valor ao LADO da barra, não embaixo: oito OPs no prazo não dizem o mesmo
                    quando as que atrasam são as caras, e a tela é de uma página só. */}
                  <div className="flex w-full items-center gap-2">
                    <BarraSegmentada
                      className="min-w-0 flex-1"
                      total={pontualidade.ops}
                      // O MESMO verde claro da ocupação e do farol "no prazo": sair no prazo
                      // é a mesma notícia boa, e dois verdes diferentes fariam a tela mentir.
                      segmentos={[{ chave: 'noPrazo', valor: pontualidade.opsNoPrazo, classe: 'bg-ocupacao-folga' }]}
                      rotulo={t('visaoExecutiva.pontualidade.barra', {
                        celula,
                        noPrazo: formatNumber(pontualidade.opsNoPrazo),
                        total: formatNumber(pontualidade.ops),
                      })}
                    />
                    <span className="shrink-0 text-xs tabular-nums text-foreground-subtle">
                      {t('visaoExecutiva.pontualidade.valor', { pct: formatPercentual(pontualidade.pctValor) })}
                    </span>
                  </div>
                </ControlledButton>
              </ControlledTooltip>
            </li>
          ))}
        </ul>

        {temRealizada ? (
          <Notice tone="neutral" size="sm" icon="task_alt" className="mt-auto">
            <p>
              {t('visaoExecutiva.pontualidade.realizadaValor', {
                pct: formatPercentual(realizada.pctDentroDoPrometido),
                dentro: formatNumber(realizada.dentroDoPrometido),
                total: formatNumber(realizada.ops),
              })}
            </p>
          </Notice>
        ) : (
          <Notice tone="neutral" size="sm" icon="pending" className="mt-auto">
            <p>{t('visaoExecutiva.pontualidade.realizada')}</p>
          </Notice>
        )}
      </EstadoDoBloco>
    </BlocoCard>
  );
}
